import { externalApplicationStore } from 'ats/pages/external-applications/ExternalApplicationStore';
import { EXTERNAL_APPLICATION_STATUS_FILTER_OPTIONS } from 'ats/pages/open-jobs/externalApplicationOptions';
import {
	deserializeColumnState,
	serializeColumnState,
	normalizeColumnStateForCompare
} from './columnState';
import {
	resolveEntityRef,
	resolveEntityRefs,
	serializeEntityRef,
	serializeEntityRefs
} from './entityRef';
import {
	normalizeEntityRefIds,
	normalizeQueryState,
	normalizeUiState,
	pickViewQueryForView,
	captureSearchFilter,
	resolveSearchFilterForApply
} from './viewStateUtils';
import { syncFilterDrawerProps } from './syncFilterDrawerProps';

const EMPTY_QUERY = { sort: { column: 'appliedAt', asc: true } };

const EXTERNAL_APPLICATIONS_VIEW_QUERY_KEYS = [
	'text',
	'status',
	'clientId',
	'jobRoleIds',
	'jobId'
];

const ENTITY_REF_KEYS = ['clientIds', 'jobRoleIds'];

function pickExternalApplicationsQueryForView(query) {
	return pickViewQueryForView(
		query,
		EXTERNAL_APPLICATIONS_VIEW_QUERY_KEYS,
		EMPTY_QUERY.sort
	);
}

function getDefaultStatusFilter() {
	return EXTERNAL_APPLICATION_STATUS_FILTER_OPTIONS[0];
}

function resolveStatusFilter(value) {
	return (
		EXTERNAL_APPLICATION_STATUS_FILTER_OPTIONS.find(
			option => option.value === value
		) || getDefaultStatusFilter()
	);
}

export function getExternalApplicationsFactoryUiState() {
	return {
		searchFilter: null,
		clientFilter: null,
		jobRoleFilter: [],
		jobFilter: null,
		statusFilter: getDefaultStatusFilter()
	};
}

export function captureExternalApplicationsListView(page) {
	return {
		ui: {
			searchFilter: captureSearchFilter(
				page.state.searchFilter,
				page.listStore.store.query
			),
			clientIds: serializeEntityRefs(page.state.clientFilter),
			jobRoleIds: serializeEntityRefs(page.state.jobRoleFilter),
			job: serializeEntityRef(page.state.jobFilter, 'title'),
			statusFilter: page.state.statusFilter?.value ?? null
		},
		query: pickExternalApplicationsQueryForView(page.listStore.store.query),
		columnState: serializeColumnState(page.state.visibleColumns)
	};
}

export function getExternalApplicationsFactorySnapshot() {
	return {
		ui: {
			searchFilter: null,
			clientIds: null,
			jobRoleIds: null,
			job: null,
			statusFilter: null
		},
		query: {
			sort: { column: 'appliedAt', asc: true }
		},
		columnState: serializeColumnState(
			externalApplicationStore.defaultTableColumnsOptions
		)
	};
}

function buildUiStateFromSaved(ui) {
	return {
		searchFilter: ui.searchFilter || null,
		clientFilter: resolveEntityRefs(ui.clientIds) || null,
		jobRoleFilter: resolveEntityRefs(ui.jobRoleIds) || [],
		jobFilter: resolveEntityRef(ui.job, 'title'),
		statusFilter: resolveStatusFilter(ui.statusFilter)
	};
}

export function applyExternalApplicationsListView(
	page,
	snapshot,
	{ isFactoryDefault = false } = {}
) {
	const ui = buildUiStateFromSaved(snapshot.ui || {});
	ui.searchFilter = resolveSearchFilterForApply(
		ui.searchFilter,
		snapshot.query
	);
	const visibleColumns = isFactoryDefault
		? externalApplicationStore.defaultTableColumnsOptions
		: deserializeColumnState(
				snapshot.columnState,
				externalApplicationStore.tableColumnsOptions,
				externalApplicationStore.defaultTableColumnsOptions
			);

	page.listStore.store.resetQuery();
	page.listStore.store.setQuery(
		'sort',
		snapshot.query?.sort || EMPTY_QUERY.sort
	);
	page.listStore.store.setQuery('text', ui.searchFilter || null);

	const query = snapshot.query || {};
	if (query.clientId?.length) {
		page.listStore.store.setQuery('clientId', query.clientId);
	} else if (ui.clientFilter?.length) {
		page.listStore.store.setQuery(
			'clientId',
			ui.clientFilter.map(client => client.id)
		);
	}

	if (query.jobRoleIds?.length) {
		page.listStore.store.setQuery('jobRoleIds', query.jobRoleIds);
	} else if (ui.jobRoleFilter?.length) {
		page.listStore.store.setQuery(
			'jobRoleIds',
			ui.jobRoleFilter.map(role => role.id)
		);
	}

	if (query.jobId) {
		page.listStore.store.setQuery('jobId', query.jobId);
	} else if (ui.jobFilter?.id) {
		page.listStore.store.setQuery('jobId', ui.jobFilter.id);
	}

	if (query.status != null) {
		page.listStore.store.setQuery('status', query.status);
	} else if (ui.statusFilter?.value) {
		page.listStore.store.setQuery('status', ui.statusFilter.value);
	}

	return new Promise(resolve => {
		page.setState({ ...ui, visibleColumns }, () => {
			syncFilterDrawerProps(page);
			page.handlePaginationChange(1);
			resolve();
		});
	});
}

export function applyExternalApplicationsFactoryDefaults(page) {
	return applyExternalApplicationsListView(
		page,
		getExternalApplicationsFactorySnapshot(),
		{ isFactoryDefault: true }
	);
}

export function resolveExternalApplicationsViewSnapshot(view) {
	if (view.systemKey === 'default') {
		return getExternalApplicationsFactorySnapshot();
	}
	return {
		ui: view.filterState?.ui || {},
		query: view.filterState?.query || EMPTY_QUERY,
		columnState: view.columnState
	};
}

export function normalizeExternalApplicationsSnapshot(snapshot) {
	const ui = normalizeUiState(snapshot.ui || {});

	ENTITY_REF_KEYS.forEach(key => {
		if (ui[key] != null) {
			ui[key] = normalizeEntityRefIds(ui[key]);
		}
	});
	if (ui.job != null) {
		ui.job = normalizeEntityRefIds([ui.job]);
	}

	return {
		ui,
		query: normalizeQueryState(
			pickExternalApplicationsQueryForView(snapshot.query || {}),
			EMPTY_QUERY.sort
		),
		columnState: normalizeColumnStateForCompare(
			snapshot.columnState,
			externalApplicationStore.defaultTableColumnsOptions
		)
	};
}

export function serializeExternalApplicationsViewForSave(snapshot) {
	return {
		filterState: {
			ui: snapshot.ui,
			query: snapshot.query
		},
		columnState: snapshot.columnState
	};
}
